import { Button } from '@/components/ui/button';
import { Settings } from 'lucide-react';

const VAULT_IMG = 'https://images.unsplash.com/photo-1710981855156-1dd4b48e668d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzZWN1cmUlMjB2YXVsdCUyMGFic3RyYWN0JTIwbG9ja3xlbnwwfHx8fDE3NzUyNjgwMTZ8MA&ixlib=rb-4.1.0&q=85';

export default function PPHVSection({ onOpenArchitecture }) {
  return (
    <section className="max-w-5xl mx-auto px-6 pt-1 pb-2" data-testid="pphv-section">
      <details className="group bg-slate-50 border-2 border-slate-300 rounded-2xl overflow-hidden">
        <summary
          className="cursor-pointer list-none px-5 py-3 flex items-center justify-between bg-white hover:bg-slate-50 select-none"
          data-testid="pphv-toggle"
        >
          <span className="text-sm sm:text-base font-bold text-[#0369A1]">
            <span className="inline-block w-5 h-5 rounded-full bg-[#0369A1] text-white text-[11px] font-bold text-center leading-5 mr-2">i</span>
            Patient Portable Health Vault (PPHV) — free forever for patients
            <span className="text-xs font-normal text-slate-500 ml-2">(tap to expand)</span>
          </span>
          <span className="text-[#0369A1] group-open:rotate-180 transition-transform text-lg">▾</span>
        </summary>
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-slate-200" data-testid="pphv-card">
          <div className="flex flex-col md:flex-row gap-6 items-start mb-6">
            <div className="flex-1">
              <p className="text-sm text-black mb-3">
                Your health vault remains secure and under your control — similar to how you control access to your bank account.
              </p>
            </div>
            <div className="w-full md:w-48 flex-shrink-0">
              <img src={VAULT_IMG} alt="Patient Portable Health Vault" className="w-full h-auto rounded-xl border border-slate-200 shadow-sm" data-testid="pphv-vault-image" />
            </div>
          </div>

          <p className="text-sm text-black mb-3">
            P.AI Global Health (Patient AI) provides every individual with a Patient Portable Health Vault (PPHV) — a secure personal health record that belongs to you.
          </p>
          <p className="text-sm text-black mb-3">
            Your diagnoses, medications, laboratory results, imaging, and medical history are organized in one place.
          </p>
          <p className="text-sm text-black mb-4">
            Your health history should travel with you — not stay trapped in hospital systems, EHRs, or EMRs.
          </p>

          <h4 className="font-bold text-sm text-black mb-2">Key Capabilities</h4>
          <ul className="list-disc pl-5 space-y-1 text-sm text-black mb-5">
            <li>Multimodal intake — text, conversation, document upload, and lab report scanning</li>
            <li>Multilingual access — interact in your own language</li>
            <li>Portable worldwide — share with any physician, clinic, or hospital</li>
          </ul>

          <h4 className="font-bold text-sm text-black mb-2">For Physicians</h4>
          <p className="text-sm text-black mb-2 font-semibold">
            Patient voice → Structured clinical intelligence of past medical history for the physicians / providers for review
          </p>
          <p className="text-sm text-black mb-2">
            P.AI Global Health (Patient AI) does <strong>not</strong> interpret the patient's history. It <strong>assembles</strong> it. The patient brings together records from disparate sources — primary care, specialists, ER visits, foreign hospitals, pharmacies — that have likely never lived together inside any single EMR or EHR your practice can see. Under the <strong>21st Century Cures Act</strong>, the patient has the right to consolidate that record across providers. P.AI is where they do it.
          </p>
          <p className="text-sm text-black mb-2">
            The output is a structured clinical brief in a SOAP format physicians already know how to read. It surfaces past clinical flags from sources your system never had access to — old allergies, prior surgical complications, medication interactions, missed screenings — so you can spend the visit on the <strong>chief complaint</strong> instead of reconstructing history.
          </p>
          <p className="text-sm text-black mb-2">
            You choose the view: a <strong>chronological past-history timeline</strong> alone, or past history <strong>plus a draft SOAP</strong> intended for your review and sign-off. Nothing is invented. No large language model touches the clinical decision layer. The physician remains the sole diagnostic decision-maker.
          </p>
          <p className="text-xs text-slate-600 italic mb-5 border-l-2 border-amber-400 pl-3">
            <strong>Not HIPAA-compliant.</strong> This is a synthetic-data sandbox. The production architecture is designed to be HIPAA-Eligible.
          </p>

          <h4 className="font-bold text-sm text-black mb-2">Design Principle</h4>
          <p className="text-sm text-black mb-2">Technology alone does not solve healthcare challenges.</p>
          <p className="text-sm text-black mb-2">Real impact comes from building systems that can be adopted widely.</p>
          <p className="text-sm text-black mb-2">Large populations often have far fewer physicians than patients.</p>
          <p className="text-sm text-black mb-2">
            AI can help bridge this gap by structuring patient information so physicians can focus on clinical decisions.
          </p>
          <p className="text-sm text-black mb-2">
            P.AI Global Health (Patient AI) follows this principle by transforming patient input into structured clinical intelligence while keeping physicians fully in control.
          </p>
          <p className="text-sm text-black mb-5">
            Healthcare infrastructure must work at population scale, not just within individual hospital systems.
          </p>

          <Button
            onClick={onOpenArchitecture}
            className="rounded-md bg-[#2563EB] hover:bg-blue-700 text-white text-sm px-5 py-2.5"
            data-testid="see-architecture-btn"
          >
            <Settings className="w-4 h-4 mr-2" /> See System Architecture
          </Button>
        </div>
      </details>
    </section>
  );
}
